import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';

import { PropertyForm } from '../../components/properties/property-form.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { errorMessage } from '../../lib/api.ts';
import { propertyKeys, useProperty, type PropertyDetail } from '../../lib/properties-api.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

function useSaved(message: string) {
  const client = useQueryClient();
  const navigate = useNavigate();
  const notify = useToast();
  return async (saved: PropertyDetail) => {
    client.setQueryData(propertyKeys.detail(saved.id), saved);
    await client.invalidateQueries({ queryKey: propertyKeys.all });
    // Owners got the "owner" role.
    await client.invalidateQueries({ queryKey: ['contacts'] });
    notify(message, 'success');
    await navigate(`/propiedades/${saved.id}`);
  };
}

export function NewPropertyPage() {
  const saved = useSaved('Propiedad registrada como borrador.');
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <Link to="/propiedades" className="text-sm text-primary hover:underline">
          ← Propiedades
        </Link>
        <h1 className="text-2xl font-semibold">Nueva propiedad</h1>
      </div>
      <PropertyForm submitLabel="Registrar propiedad" onSaved={(p) => void saved(p)} />
    </div>
  );
}

export function EditPropertyPage() {
  const { id = '' } = useParams();
  const property = useProperty(id);
  const saved = useSaved('Cambios guardados.');
  if (property.isPending) return <Loading />;
  if (property.error) {
    return (property.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(property.error)}</Alert>
    );
  }
  if (!property.data.can.edit) return <NotFoundPage />;
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <Link to={`/propiedades/${id}`} className="text-sm text-primary hover:underline">
          ← {property.data.title}
        </Link>
        <h1 className="text-2xl font-semibold">Editar propiedad</h1>
      </div>
      <PropertyForm
        property={property.data}
        submitLabel="Guardar cambios"
        onSaved={(p) => void saved(p)}
      />
    </div>
  );
}
